import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DASHBOARDS = [
  { id: 'alcazar', label: 'Real Alcázar de Sevilla', path: '/alcazar/dashboard' },
  { id: 'home', label: 'Inicio (Plataforma)', path: '/' },
];

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const query = useQuery();
  const next = query.get('next');

  const [dashboard, setDashboard] = useState(() => {
    return localStorage.getItem('preferred_dashboard') || 'alcazar';
  });

  const onDashboardChange = (e) => {
    const v = e.target.value;
    setDashboard(v);
    localStorage.setItem('preferred_dashboard', v);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);

    if (result.success) {
      if (next) {
        navigate(next, { replace: true });
      } else {
        const chosen = DASHBOARDS.find(d => d.id === dashboard);
        navigate('/alcazar/dashboard', { replace: true });
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 w-full max-w-md p-10">
        {/* Logo Infoticketing */}
        <div className="flex flex-col items-center mb-6">
          <img
            src="/logo-infoticketing.png"
            alt="Infoticketing"
            className="h-24"
          />
        </div>

        {/* Dashboard selector */}
        <div className="mb-8">
          <div className="text-center text-lg font-semibold text-[#111827] mb-3">Dashboard</div>
          <select
            value={dashboard}
            onChange={onDashboardChange}
            className="w-full px-4 py-3 bg-[#F9FAFB] border border-gray-200 rounded-xl text-sm text-[#111827] focus:outline-none focus:border-[#3B82F6] transition-colors duration-200"
          >
            {DASHBOARDS.map(d => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
          {next && (
            <div className="mt-2 text-xs text-[#6B7280] text-center">
              Acceso directo a: <span className="font-mono">{next}</span>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-[#F9FAFB] text-[#111827] px-4 py-3 rounded-xl mb-6 text-sm border border-gray-200">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#111827] mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3.5 bg-[#F9FAFB] border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6] transition-colors duration-200 text-[#111827] placeholder:text-[#6B7280]"
              placeholder="tu@email.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[#111827] mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3.5 bg-[#F9FAFB] border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6] transition-colors duration-200 text-[#111827] placeholder:text-[#6B7280] pr-12"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#111827] transition-colors duration-200"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <p className="text-[#6B7280] text-center text-sm">Inicia sesion para continuar</p>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#3B82F6] text-white py-3.5 rounded-xl font-medium hover:bg-[#2563EB] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Iniciando sesion...
              </>
            ) : (
              'Iniciar Sesion'
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-[#6B7280] text-sm mt-10">
          Manantial de ideas SL &copy; 2026
        </p>
      </div>
    </div>
  );
}
